/**
 * SOLID Review
 * S — Good. Presentation of the consent choice: the first-party preferences modal's open state, the
 *     "Cookie settings" button (rendered by the screen that wants it — see CookieSettingsButton), and the
 *     bridge that lets the legal footers open it. Booting the CMP,
 *     keeping its state and the audit record are the Application's since strict-ports FE4b.
 * O — Good. A new entry point calls `openCookiePreferences()`; a new category is the modal's and the
 *     consent schema's, not this file's.
 * L — N/A — no inheritance hierarchy.
 * I — Acceptable. Three public surfaces: the provider, the `CookieSettingsButton` a screen places where
 *     it wants it, and the module-level `openCookiePreferences()` the legal footers call — one way in for
 *     components that do not render the provider.
 * D — Good. The client view and three use cases, through the framework's client hooks; the modal.
 */
import React, { useEffect, useState } from 'react';
import { useClientView, useClientAction } from '../client/useClientPorts';
import type { TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';
import type { IEntityCookieConsentCategories } from '../../application/ports/ClientVocabulary';
import { CookiePreferencesModal } from './CookiePreferencesModal';

const START_CONSENT: TClientUseCaseInput<'startConsent'> = {};
const OPEN_PREFERENCES: TClientUseCaseInput<'openCookiePreferences'> = {};

// The mounted provider's opener: the legal footers' "Manage cookies" reach the provider through it.
let openMounted: (() => void) | null = null;
// The mounted provider's first-party modal opener: the "Cookie settings" button reaches it through this.
let openModalMounted: (() => void) | null = null;

/**
 * THE COOKIE-CONSENT UI (frontend strict-ports plan, FE4b, 2026-10-04). Until FE4b this file was also the
 * CMP's driver: it subscribed to the gateway, wrote a consent store, kept the last recorded signature in
 * a module variable and posted the audit record itself, over a gateway and a service App.tsx injected
 * through `configureConsent`. Now:
 *
 *   - `startConsent` boots the CMP (idempotent; the Infrastructure adapter subscribes once), and every
 *     state it reports reaches the session as a `consentChanged` event — the decision keeps it and asks
 *     for the audit record when a resolved choice changes;
 *   - the modal opens on the session's consent, read from the client view, and saving is
 *     `saveCookiePreferences`;
 *   - `openCookiePreferences()` asks the certified CMP first (`openCookiePreferences` answers whether it
 *     could present its UI) and falls back to the first-party modal — also when the platform could not
 *     be asked, so "Cookie settings" always opens something.
 *
 * The provider renders inside `ComponentFrameworkProvider` now (it reads the client view; flagged in
 * §28.201).
 */
export const CookieConsentProvider: React.FC = () => {
  const categories = useClientView((s) => s.session.consent.categories);
  const startConsent = useClientAction('startConsent');
  const openPreferences = useClientAction('openCookiePreferences');
  const savePreferences = useClientAction('saveCookiePreferences');
  const [prefsOpen, setPrefsOpen] = useState(false);

  useEffect(() => {
    void startConsent(START_CONSENT);
  }, [startConsent]);

  useEffect(() => {
    openModalMounted = () => setPrefsOpen(true);
    return () => { openModalMounted = null; };
  }, []);

  useEffect(() => {
    openMounted = () => {
      void openPreferences(OPEN_PREFERENCES).then((answer) => {
        if (!(answer.success && answer.value.opened)) setPrefsOpen(true);
      });
    };
    return () => { openMounted = null; };
  }, [openPreferences]);

  const handleSave = (chosen: IEntityCookieConsentCategories): void => {
    const command: TClientUseCaseInput<'saveCookiePreferences'> = { categories: chosen };
    void savePreferences(command);
    setPrefsOpen(false);
  };

  return (
    <>
      <CookiePreferencesModal
        isOpen={prefsOpen}
        initial={categories}
        onSave={handleSave}
        onClose={() => setPrefsOpen(false)}
      />
    </>
  );
};

/**
 * The "Cookie settings" control. It is rendered IN THE FLOW of the screen that places it (the welcome
 * screen's footer row): until 2026-10-07 it was a `position: fixed` pill pinned to the bottom-left of the
 * viewport, which on a phone sat on top of the footer text — and AdSense reviewers flag overlapping
 * content. The provider keeps the modal; this opens it. A no-op while no provider is mounted.
 */
export const CookieSettingsButton: React.FC = () => (
  <button
    type="button"
    onClick={() => openModalMounted?.()}
    data-testid="cookie-settings-button"
    aria-label="Cookie settings"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      minHeight: 36,
      padding: '6px 12px',
      borderRadius: 8,
      border: '1px solid rgba(255,255,255,0.18)',
      background: 'rgba(0,0,0,0.55)',
      color: 'rgba(255,255,255,0.75)',
      fontSize: 12,
      fontFamily: 'system-ui, sans-serif',
      letterSpacing: '0.04em',
      cursor: 'pointer',
    }}
  >
    {/* A cookie with a bite out of it and four chips — drawn here, so there is no icon library to ship. */}
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.6A9 9 0 1 1 11.4 3 4.6 4.6 0 0 0 16 7.6a4.6 4.6 0 0 0 5 5z" />
      <circle cx="8.5" cy="9" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="13" cy="14" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="8" cy="15" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="15.5" cy="17.5" r="0.9" fill="currentColor" stroke="none" />
    </svg>
    <span>Cookie settings</span>
  </button>
);

/**
 * Open the cookie preferences: the certified CMP when it can present its UI, else the first-party modal.
 * A no-op while no provider is mounted.
 */
export function openCookiePreferences(): void {
  openMounted?.();
}
